import { Alert, Button, Col, Form, Input, Popover, Radio, Row, Select, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";

import { approverApi, toArray } from "../../../../Services";
import { usePanelOrgTree } from "../../../../Hooks/usePanelOrgTree";
import {
  ApproverEntry,
  ReviewerEntry,
  WorkOrderFormValues,
  approvalAuthorities,
} from "../../Constants";
import { allowedDomainEmail, permittedDomainsMessage } from "../../../../Utils/Constant";
import styles from "../../WorkOrder.module.scss";

interface Props {
  values: WorkOrderFormValues;
  authority: string;
  setAuthority: (authority: string) => void;
  reviewers: ReviewerEntry[];
  setReviewers: (reviewers: ReviewerEntry[]) => void;
  approvers: ApproverEntry[];
  setApprovers: (approvers: ApproverEntry[]) => void;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  isSavingDraft?: boolean;
  /** A submitted work order: its saved approver chain stands as routed. A
      draft's approvers follow Approval Management as it is now. */
  keepSavedRouting?: boolean;
}

export const blankReviewer = (seed: number): ReviewerEntry => ({
  key: `reviewer-${seed}`,
  name: "",
  email: "",
});

const clean = (value: any) => String(value ?? "").trim();
/* The API spells designations in upper case ("HEAD OF SUBSIDIARY") while the
   UI shows them in title case, so every comparison folds case. */
const designationKey = (value: any) => clean(value).toUpperCase();

/** A configured approver offered for one designation. */
interface Candidate {
  name: string;
  email: string;
}

/**
 * Every R1 configured for the cost centre approves, and so does every APC
 * Secretariat and APC Member (as e-POD lists them), so they are laid out one
 * row each, read-only, rather than offered as a choice. Every other
 * designation is a single step in the chain, so its candidates go in a
 * dropdown — and so does R1 under Head of Subsidiary, where one R1 is picked.
 */
const LISTED_IN_FULL = ["R1", "APC SECRETARIAT", "APC MEMBER"];
const listedInFull = (designation: any, groupKey?: string) =>
  LISTED_IN_FULL.includes(designationKey(designation)) &&
  !(groupKey === "headOfSubsidiary" && designationKey(designation) === "R1");

/** Read-only fields are shaded so it is obvious they cannot be typed into. */
const readOnlyField = { background: "#f5f5f5", cursor: "not-allowed" };

const InfoHint = ({ text }: { text: string }) => (
  <Popover
    content={<p style={{ maxWidth: 320, marginBottom: 0 }}>{text}</p>}
    placement="topLeft"
  >
    <Icon
      icon="bi:info-circle"
      className={styles.infoHint}
    />
  </Popover>
);

/**
 * The (i) beside each Approval Authority, with e-POD's wording. Default on the
 * icon is prevented because it sits inside the radio's label, where a click
 * would otherwise select that authority as well.
 */
const PteHint = ({ title, pte }: { title: string; pte?: string }) =>
  pte ? (
    <span
      role="button"
      aria-label={`${title} procurement threshold`}
      onClick={(event) => event.preventDefault()}
    >
      <InfoHint text={pte} />
    </span>
  ) : null;

/**
 * Step 4 — Reviewers.
 *
 * Choosing an Approval Authority fixes the approval chain: the designations
 * for that group (R1…GCEO etc.) are laid out as read-only rows and each is
 * filled from the approvers configured for this subsidiary and cost centre.
 *
 * A designation can have more than one approver configured. Only the first was
 * ever used, which quietly hid the rest: R1 now gets a row per approver, and
 * the other designations offer theirs in a dropdown. Where nothing is
 * configured the row stays free-form, so a one-off substitution is still
 * possible without going through Approval Management.
 *
 * Reviewers are free-form name/email pairs and are not part of that chain.
 */
export const Reviewers = ({
  values,
  authority,
  setAuthority,
  reviewers,
  setReviewers,
  approvers,
  setApprovers,
  onBack,
  onNext,
  onSaveDraft,
  isSavingDraft,
  keepSavedRouting = false,
}: Props) => {
  const group =
    approvalAuthorities.find((entry) => entry.key === authority) ??
    approvalAuthorities.find((entry) => entry.authorityGroup === authority);
  /* What the current approver rows were built for. Rebuilding is keyed off
     this rather than off the raw effect deps so that (a) a routing loaded for
     an existing work order keeps its row ids, and (b) stepping away and back
     does not discard manual name/email edits. */
  const signature = `${authority}|${values.subsidiary}|${values.costCentre}|${values.budgetStatus}`;
  /* Approver records hold the subsidiary as a name or, if saved before that
     was fixed, as its id — the resolver reads both. */
  const { resolveSubsidiary, isLoading: orgLoading } = usePanelOrgTree();
  const builtFor = useRef<string | null>(null);
  /* Configured approvers per designation, backing the dropdowns below. */
  const [candidates, setCandidates] = useState<Record<string, Candidate[]>>({});
  /* Whether this authority has ANY approver configured for the chosen cost
     centre. `null` while the answer is still being fetched, so the warning
     does not flash up before the first response. */
  const [hasApprovers, setHasApprovers] = useState<boolean | null>(null);
  /* True when the rows came from a SUBMITTED work order rather than being built
     from the configuration: that chain is already routed, so it must not be
     held hostage to what Approval Management holds today. A draft is not routed
     yet, so its approvers are rebuilt from today's configuration — reopening
     one showed the approvers (and designations) it was saved with, even after
     Approval Management had changed them. */
  const [adoptedRouting, setAdoptedRouting] = useState(false);

  /* Rebuild the approver rows whenever the authority (or the org position that
     scopes it) changes, pre-filling each designation from the API. */
  useEffect(() => {
    if (!group) return;
    /* Matching a configured approver needs the subsidiary id-to-name lookup,
       so wait for it. Running first and missing would leave the rows blank
       for good, since the guard below only lets this run once per signature. */
    if (orgLoading) return;
    if (builtFor.current === signature) return;
    /* First run with rows already present: they came from the parent (an edit
       prefill or an earlier visit to this step). A submitted work order keeps
       them — the dropdowns still get today's options. A draft's are rebuilt,
       keeping a saved choice only where that person is still configured for
       that designation. */
    const adopt = keepSavedRouting && builtFor.current === null && approvers.length > 0;
    const saved = builtFor.current === null ? approvers : [];
    builtFor.current = signature;
    setAdoptedRouting(adopt);

    setHasApprovers(null);

    if (!adopt) {
      setApprovers(
        group.mandatory.map((designation, index) => ({
          key: `approver-${index}`,
          designation,
          name: "",
          email: "",
        }))
      );
    }

    /* Fill each designation from the approvers configured for this work order.
     *
     * `/work-order/approver/get-approver-for-spa` is the route meant for this;
     * the Approvers tab's paged administration listing was only being borrowed
     * because nothing better was known. That listing stays as a fallback, so a
     * mismatch in what the new route expects cannot leave the chain blank.
     *
     * The subsidiary and cost centre are matched here as well as being sent:
     * server-side filtering on subsidiary misses records saved with the
     * subsidiary id rather than its name, so the comparison runs through
     * `resolveSubsidiary`, which reads either. */
    approverApi
      .forWorkOrder({
        authorityGroup: group.authorityGroup,
        subsidiary: values.subsidiary,
        division: values.costCentre,
        /* Only the Head of Subsidiary R2 designation is scoped by budget, but
           sending it always costs nothing and keeps the call uniform. */
        budget: values.budgetStatus,
      })
      .then((result: any) =>
        toArray(result).length
          ? result
          : approverApi.list({ authorityGroup: group.authorityGroup, page: 0, size: 1000 })
      )
      .catch(() => approverApi.list({ authorityGroup: group.authorityGroup, page: 0, size: 1000 }))
      .then((result: any) => {
        const configured = toArray(result).filter(
          (record: any) =>
            resolveSubsidiary(record.subsidiary) === values.subsidiary &&
            clean(record.division) === clean(values.costCentre)
        );

        /* Every configured approver for a designation, not just the first. */
        const byDesignation: Record<string, Candidate[]> = {};
        configured.forEach((record: any) => {
          const designation = designationKey(record.designation);
          if (!designation) return;
          const candidate = { name: clean(record.userName), email: clean(record.userEmail) };
          const existing = byDesignation[designation] ?? [];
          // The same person configured twice is one choice, not two.
          if (existing.some((entry) => entry.email === candidate.email)) return;
          byDesignation[designation] = [...existing, candidate];
        });
        setCandidates(byDesignation);
        setHasApprovers(Object.keys(byDesignation).length > 0);

        if (adopt) return; // the loaded rows stand; they now have options

        /* Mandatory designations are always steps, one row each.
         *
         * The optional ones are ALTERNATIVES, not extra steps: an authority
         * named "MD/GCEO" routes to the MD or the GCEO, and
         * "GCFO/CFO/CAO/COO" to one of those four. They share a single row
         * whose Designation is chosen — giving them a row apiece is what
         * produced two rows, GCEO and MD, for one approval. */
        const chain = [...group.mandatory];
        const configuredOptional = (designation: string) =>
          (byDesignation[designationKey(designation)] ?? []).length > 0;
        /* The alternative the draft was saved with, if it is still configured;
           else the first one that is. */
        const savedOptional = group.optional.find(
          (designation) =>
            configuredOptional(designation) &&
            saved.some((row) => designationKey(row.designation) === designationKey(designation))
        );
        const firstOptional = savedOptional ?? group.optional.find(configuredOptional);
        if (firstOptional) chain.push(firstOptional);

        const built: ApproverEntry[] = [];
        chain.forEach((designation) => {
          const matches = byDesignation[designationKey(designation)] ?? [];

          if (listedInFull(designation, group.key) && matches.length > 1) {
            matches.forEach((match, index) =>
              built.push({
                key: `approver-${designation}-${index}`,
                designation,
                name: match.name,
                email: match.email,
              })
            );
            return;
          }

          /* The draft's own pick, while that person is still configured here. */
          const kept = saved.find(
            (row) =>
              designationKey(row.designation) === designationKey(designation) &&
              matches.some((match) => match.email.toLowerCase() === clean(row.email).toLowerCase())
          );
          built.push({
            key: `approver-${designation}`,
            designation,
            name: kept?.name ?? matches[0]?.name ?? "",
            email: kept?.email ?? matches[0]?.email ?? "",
          });
        });
        setApprovers(built);
      })
      .catch(() => setHasApprovers(false));
    // eslint-disable-next-line
  }, [signature, orgLoading]);

  /**
   * A reviewer must be someone inside the organisation.
   *
   * The permitted domains come from the runtime config rather than being
   * written here, so this screen cannot drift from the rest of the app — the
   * same list already gates the user onboarding and Send Document forms. Only
   * reported once something has been typed, so an untouched row is not nagged
   * at, and the rows are optional in the first place.
   */
  const reviewerEmailError = (reviewer: ReviewerEntry) => {
    const email = clean(reviewer.email);
    if (!email) return undefined;
    return allowedDomainEmail.test(email) ? undefined : permittedDomainsMessage;
  };

  /**
   * The authority's alternatives, narrowed to those actually configured.
   *
   * These share one row: an approval goes to the MD or the GCEO, not both.
   */
  const alternatives = (group?.optional ?? []).filter(
    (designation: string) => (candidates[designationKey(designation)] ?? []).length
  );
  const isAlternative = (designation: any) =>
    (group?.optional ?? []).some(
      (entry: string) => designationKey(entry) === designationKey(designation)
    );

  /* Every reviewer row must be complete, and addressed inside the
     organisation, before another can be started. */
  const canAddReviewer = reviewers.every(
    (reviewer) => clean(reviewer.name) && clean(reviewer.email) && !reviewerEmailError(reviewer)
  );

  const updateReviewer = (key: string, patch: Partial<ReviewerEntry>) =>
    setReviewers(reviewers.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const updateApprover = (key: string, patch: Partial<ApproverEntry>) =>
    setApprovers(approvers.map((a) => (a.key === key ? { ...a, ...patch } : a)));

  const isValid =
    !!authority &&
    /* Nothing configured for this authority means there is no chain to route
       to, so the step cannot be completed — typing names into the blank rows
       would send the work order to people the approval setup does not know. */
    /* Nothing configured means there is no chain to route to — unless the
       work order already carries one, which is the case when a draft is
       reopened for editing. */
    (hasApprovers === true || adoptedRouting) &&
    approvers.length > 0 &&
    approvers.every((a) => a.name && a.email) &&
    /* At least one reviewer, complete. A wholly blank row used to pass, so
       Next was available with no reviewer named at all — which showed up on
       the authorities whose approvers are fully configured, Head of
       Subsidiary among them, since nothing else was left to block it. */
    reviewers.some((r) => clean(r.name) && clean(r.email)) &&
    reviewers.every((r) => (r.name && r.email) || (!r.name && !r.email)) &&
    /* An address outside the permitted domains cannot go into the routing. */
    reviewers.every((r) => !reviewerEmailError(r));

  return (
    <>
      <Typography.Title level={4}>
        Approval Authority <InfoHint text="Select Approval authority based on GALOA" />
      </Typography.Title>

      <Radio.Group
        value={authority}
        onChange={(e) => setAuthority(e.target.value)}
        className={styles.authorityGroup}
      >
        {approvalAuthorities.map((entry) => (
          <Radio
            value={entry.key}
            key={entry.key}
          >
            {entry.label} <PteHint title={entry.label} pte={entry.pte} />
          </Radio>
        ))}
      </Radio.Group>

      <Typography.Title
        level={4}
        style={{ marginTop: 24 }}
      >
        Reviewers details <InfoHint text="Name of the reviewer" />
      </Typography.Title>

      <Form layout="vertical">
        {reviewers.map((reviewer) => (
          <Row
            gutter={24}
            key={reviewer.key}
            align="top"
          >
            <Col
              xs={24}
              sm={10}
            >
              <Form.Item required label="Name">
                <Input
                  value={reviewer.name}
                  onChange={(e) => updateReviewer(reviewer.key, { name: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={10}
            >
              <Form.Item required
                label="Email"
                validateStatus={reviewerEmailError(reviewer) ? "error" : undefined}
                help={reviewerEmailError(reviewer)}
              >
                <Input
                  value={reviewer.email}
                  onChange={(e) => updateReviewer(reviewer.key, { email: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col>
              <Icon
                icon="fluent:delete-48-regular"
                className={styles.adminRowDelete}
                onClick={() =>
                  setReviewers(
                    reviewers.length === 1
                      ? [blankReviewer(0)]
                      : reviewers.filter((r) => r.key !== reviewer.key)
                  )
                }
              />
            </Col>
          </Row>
        ))}

        <Row justify="end">
          <Col>
            <span
              className={canAddReviewer ? styles.addAnother : styles.addAnotherDisabled}
              onClick={() =>
                canAddReviewer && setReviewers([...reviewers, blankReviewer(reviewers.length + 1)])
              }
            >
              <Icon
                icon="akar-icons:plus"
                inline
              />
              Add Another
            </span>
          </Col>
        </Row>

        <Typography.Title
          level={4}
          style={{ marginTop: 24 }}
        >
          Approvers Details{" "}
          <InfoHint text="Automated base on Approval Authority Selection" />
        </Typography.Title>

        {!group && <Typography.Text type="secondary">Select an Approval Authority to see its approvers.</Typography.Text>}

        {hasApprovers === false && !adoptedRouting && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="No approver is configured for the selected Approval Authority."
            description="Add approvers for this Subsidiary/Division and Cost Centre in Approval Management, then continue."
          />
        )}

        {approvers.map((approver) => {
          const options = candidates[designationKey(approver.designation)] ?? [];
          /* R1 / APC Secretariat / APC Member are listed in full rather than
             chosen from (bar R1 under Head of Subsidiary), and a designation
             with nothing configured stays free-form. */
          const offersChoice = options.length > 0 && !listedInFull(approver.designation, group?.key);

          return (
            <Row
              gutter={24}
              key={approver.key}
            >
              <Col
                xs={24}
                sm={4}
              >
                <Form.Item required label="Designation">
                  {isAlternative(approver.designation) ? (
                    /* The authority's alternatives — pick which one approves. */
                    <Select
                      showSearch
                      optionFilterProp="children"
                      value={approver.designation || undefined}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        const first = (candidates[designationKey(value)] ?? [])[0];
                        updateApprover(approver.key, {
                          designation: value,
                          name: first?.name ?? "",
                          email: first?.email ?? "",
                        });
                      }}
                    >
                      {alternatives.map((designation: string, index: number) => (
                        <Select.Option value={designation} key={`${index}`}>
                          {designation}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    /* Fixed by the chosen authority — not user-editable. */
                    <Input
                      value={approver.designation}
                      disabled
                    />
                  )}
                </Form.Item>
              </Col>
              <Col
                xs={24}
                sm={10}
              >
                <Form.Item required label="Name">
                  {offersChoice ? (
                    <Select
                      showSearch
                      optionFilterProp="children"
                      value={approver.name || undefined}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        const match = options.find((entry) => entry.name === value);
                        updateApprover(approver.key, {
                          name: value,
                          email: match?.email ?? approver.email,
                        });
                      }}
                    >
                      {options.map((entry, index) => (
                        <Select.Option
                          value={entry.name}
                          key={`${index}`}
                        >
                          {entry.name}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    /* Read-only. An approver is whoever Approval Management
                       configured, so typing a name here invented a step in the
                       chain that no configuration backs — and with no address
                       beside it, the row could never be sent anyway. R1 lands
                       here too: it is listed in full rather than chosen from,
                       but it is still not the user's to write. */
                    <Input
                      value={approver.name}
                      readOnly
                      style={readOnlyField}
                      placeholder="No approver configured"
                    />
                  )}
                </Form.Item>
              </Col>
              <Col
                xs={24}
                sm={10}
              >
                <Form.Item required label="Email ID">
                  {/* The address belongs to whoever is named beside it, so it
                      follows the Name rather than being typed. One person may
                      hold more than one designation in a chain — that is a
                      configuration decision, not something to warn about
                      here. */}
                  <Input
                    value={approver.email}
                    readOnly
                    style={readOnlyField}
                  />
                </Form.Item>
              </Col>
            </Row>
          );
        })}
      </Form>

      <Row
        justify="end"
        gutter={16}
        className={styles.stepActions}
      >
        <Col>
          <Button onClick={onBack}>Back</Button>
        </Col>
        <Col>
          <Button
            loading={isSavingDraft}
            onClick={onSaveDraft}
          >
            Save as draft
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            disabled={!isValid}
            onClick={onNext}
          >
            Next
          </Button>
        </Col>
      </Row>
    </>
  );
};
